import bindAll from 'lodash.bindall';
import React from 'react';
import PropTypes from 'prop-types';

import monitorAdapter from '../lib/monitor-adapter.js';
import MonitorComponent, {monitorModes} from '../components/monitor/monitor.jsx';
import {addMonitorRect, getInitialPosition, resizeMonitorRect, removeMonitorRect} from '../reducers/monitor-layout';

import {connect} from 'react-redux';
import {Map} from 'immutable';
import {VM} from '@hetao/scratch-core';
import layoutConstant from '../lib/layout-constants';

const availableModes = opcode => (
    monitorModes.filter(t => {
        if (opcode === 'data_variable') {
            return t !== 'list';
        } else if (opcode === 'data_listcontents') {
            return t === 'list';
        }
        return t !== 'slider' && t !== 'list';
    })
);

class Monitor extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleDragEnd',
            'handleNextMode',
            'handleSetModeToDefault',
            'handleSetModeToLarge',
            'handleSetModeToSlider',
            'setElement',
            'updateXY'
        ]);

        // @todo consume from VM, but need to store until there are APIs to update vm
        this.state = {
            mode: props.mode || 'default'
        };
    }
    componentDidMount () {
        this.stageWidth = this.props.isFullScreen ? this.props.stageSize.width : layoutConstant.standardStageWidth;
        this.stageHeight = this.props.isFullScreen ? this.props.stageSize.height : layoutConstant.standardStageHeight;
        let rect;

        const isNum = num => typeof num === 'number' && !isNaN(num);

        // Load the VM provided position if not loaded already
        // If a monitor has numbers for the x and y positions, load the saved position.
        // Otherwise, auto-position the monitor.
        if (isNum(this.props.x) && isNum(this.props.y) &&
            !this.props.monitorLayout.savedMonitorPositions[this.props.id]) {
            let x = this.props.x;
            let y = this.props.y;
            //dx dy机制考虑删除，非最小尺寸时显示不正确
            // if (isNum(this.props.dx) && isNum(this.props.dy)) {
            //     x = this.props.dx * this.stageWidth;
            //     y = this.props.dy * this.stageHeight;
            // }
            rect = {
                upperStart: {x, y},
                lowerEnd: {x: x + this.element.offsetWidth, y: y + this.element.offsetHeight}
            };
            this.props.addMonitorRect(this.props.id, rect, true /* savePosition */);
        } else { // Newly created user monitor
            rect = getInitialPosition(
                this.props.monitorLayout, this.props.id, this.element.offsetWidth, this.element.offsetHeight);
            this.props.addMonitorRect(this.props.id, rect);
            // save monitor by cjr
            this.updateXY(rect.upperStart.x, rect.upperStart.y);
        }
        this.element.style.top = `${rect.upperStart.y}px`;
        this.element.style.left = `${rect.upperStart.x}px`;
    }

    shouldComponentUpdate (nextProps, nextState) {
        if (nextState !== this.state) {
            return true;
        }
        for (const key of Object.getOwnPropertyNames(nextProps)) {
            // Don't need to rerender when other monitors are moved.
            // monitorLayout is only used during initial layout.
            if (key !== 'monitorLayout' && nextProps[key] !== this.props[key]) {
                return true;
            }
        }
        return false;
    }
    componentDidUpdate () {
        this.props.resizeMonitorRect(this.props.id, this.element.offsetWidth, this.element.offsetHeight);
    }
    componentWillUnmount () {
        this.props.removeMonitorRect(this.props.id);
    }
    updateXY (x, y) {
        const rect = {
            id: this.props.id,
            x,
            y,
            dx: x / this.stageWidth,
            dy: y / this.stageHeight
        };
        this.props.vm.runtime.requestUpdateMonitor(Map(rect));
        return rect;
    }
    handleDragEnd (e, {x, y}) {
        const newX = parseInt(this.element.style.left, 10) + x;
        const newY = parseInt(this.element.style.top, 10) + y;
        // save monitor by cjr
        const rect = this.updateXY(newX, newY);
        this.props.onDragEnd(
            this.props.id,
            rect.dx * this.stageWidth,
            rect.dy * this.stageHeight
        );
    }
    handleNextMode () {
        const modes = availableModes(this.props.opcode);
        const modeIndex = modes.indexOf(this.state.mode);
        const newMode = modes[(modeIndex + 1) % modes.length];
        this.setState({mode: newMode});
        // save monitor by cjr
        this.props.vm.runtime.requestUpdateMonitor(Map({
            id: this.props.id,
            mode: newMode
        }));
    }
    handleSetModeToDefault () {
        this.setState({mode: 'default'});
        // save monitor by cjr
        this.props.vm.runtime.requestUpdateMonitor(Map({
            id: this.props.id,
            mode: 'default'
        }));
    }
    handleSetModeToLarge () {
        this.setState({mode: 'large'});
        // save monitor by cjr
        this.props.vm.runtime.requestUpdateMonitor(Map({
            id: this.props.id,
            mode: 'large'
        }));
    }
    handleSetModeToSlider () {
        this.setState({mode: 'slider'});
        // save monitor by cjr
        this.props.vm.runtime.requestUpdateMonitor(Map({
            id: this.props.id,
            mode: 'slider'
        }));
    }
    setElement (monitorElt) {
        this.element = monitorElt;
    }
    render () {
        const monitorProps = monitorAdapter(this.props);
        const showSliderOption = availableModes(this.props.opcode).indexOf('slider') !== -1;
        return (
            <MonitorComponent
                componentRef={this.setElement}
                {...monitorProps}
                draggable={this.props.draggable}
                height={this.props.height}
                max={this.props.max}
                min={this.props.min}
                mode={this.state.mode}
                targetId={this.props.targetId}
                width={this.props.width}
                onDragEnd={this.handleDragEnd}
                onNextMode={this.handleNextMode}
                onSetModeToDefault={this.handleSetModeToDefault}
                onSetModeToLarge={this.handleSetModeToLarge}
                onSetModeToSlider={showSliderOption ? this.handleSetModeToSlider : null}
            />
        );
    }
}

Monitor.propTypes = {
    addMonitorRect: PropTypes.func.isRequired,
    draggable: PropTypes.bool,
    dx: PropTypes.number,
    dy: PropTypes.number,
    height: PropTypes.number,
    id: PropTypes.string.isRequired,
    isFullScreen: PropTypes.bool.isRequired,
    max: PropTypes.number,
    min: PropTypes.number,
    mode: PropTypes.oneOf(['default', 'slider', 'large', 'list']),
    monitorLayout: PropTypes.shape({
        monitors: PropTypes.object,
        savedMonitorPositions: PropTypes.object
    }).isRequired,
    onDragEnd: PropTypes.func.isRequired,
    opcode: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    params: PropTypes.object, // eslint-disable-line react/no-unused-prop-types, react/forbid-prop-types
    removeMonitorRect: PropTypes.func.isRequired,
    resizeMonitorRect: PropTypes.func.isRequired,
    spriteName: PropTypes.string, // eslint-disable-line react/no-unused-prop-types,
    stageSize: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number
    }).isRequired,
    targetId: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]))
    ]), // eslint-disable-line react/no-unused-prop-types
    vm: PropTypes.instanceOf(VM),
    width: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number
};
const mapStateToProps = state => ({
    monitorLayout: state.scratchGui.monitorLayout,
    vm: state.scratchGui.vm
});
const mapDispatchToProps = dispatch => ({
    addMonitorRect: (id, rect, savePosition) =>
        dispatch(addMonitorRect(id, rect.upperStart, rect.lowerEnd, savePosition)),
    resizeMonitorRect: (id, newWidth, newHeight) => dispatch(resizeMonitorRect(id, newWidth, newHeight)),
    removeMonitorRect: id => dispatch(removeMonitorRect(id))
});
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Monitor);
